// Offline mode — port of globals.offlineMode + offline_prefs.dart +
// connectionHadler.dart.
//
// The SQLite cache lets the app SHOW everything without a network. This module
// is what lets it BEHAVE well without one: knowing we're offline means not
// firing requests that are certain to fail, and routing control over BLE
// instead of MQTT.
//
// Three independent sources, OR'd together:
//
//   manual            the user's toggle on the Me tab, persisted across restarts
//   noNetwork         no usable network interface at all (the Dart has this too)
//   cloudUnreachable  the link is fine but the BROKER is not — see below
//
// ⚠️ "No network" is NOT "Wi-Fi is off". REST and MQTT run over ANY transport —
// Wi-Fi, mobile data, ethernet, VPN — so this is only true when every one of
// them is gone. Getting that wrong drops a perfectly online app into BLE mode.

import { Network } from '@capacitor/network';
import { isNativePlatform } from '../platform';

const KEY = 'sli_offline_manual';

let manual = false;
let noNetwork = false;
const listeners = new Set();

function emit() {
  const v = isOffline();
  listeners.forEach((fn) => { try { fn(v); } catch (e) { /* ignore */ } });
}

// The effective state — what every caller should consult.
export function isOffline() {
  return manual || noNetwork || cloudUnreachable;
}

export const isManualOffline = () => manual;
export const hasNoNetwork = () => noNetwork;

export function onOfflineChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Persisted separately from the user record so it survives a re-login and isn't
// clobbered when the user row is refreshed from the server — the same reasoning
// as the Dart keeping it in its own file.
export function loadManual() {
  try {
    manual = localStorage.getItem(KEY) === '1';
  } catch (e) {
    manual = false;
  }
  return manual;
}

// ── The third source: the broker is unreachable ────────────────────────────
//
// ⚠️ "CONNECTED TO WI-FI" IS NOT "HAS INTERNET", and conflating them left the
// app dead in the most common outage there is. The Network plugin and
// navigator.onLine both report the LINK. With the router up and the ISP down,
// both say online, `isOffline()` stayed false, and every command was published
// into an MQTT client that could not reach anything — silently, because
// publishing to a disconnected client is not an error.
//
// Meanwhile the device itself HAS noticed (its firmware probes the broker and
// drops to BLE within seconds of finding it unreachable), so it is sitting
// right there advertising while the app talks to the cloud. This is what closes
// that gap: mqttClient reports the broker's reachability, and control routes
// over BLE like any other offline case.
//
// Kept separate from `noNetwork` rather than folded into it because they are
// different facts and are recovered differently — the link comes back on its
// own, the broker needs a successful connection to prove it.
let cloudUnreachable = false;

export const isCloudUnreachable = () => cloudUnreachable;

export function setCloudUnreachable(value) {
  const v = !!value;
  if (v === cloudUnreachable) return; // don't churn listeners on every retry
  cloudUnreachable = v;
  emit();
}

export function setManual(value) {
  manual = !!value;
  try {
    localStorage.setItem(KEY, manual ? '1' : '0');
  } catch (e) { /* ignore */ }
  emit();
}

// Start watching connectivity. Safe to call more than once.
export async function initOfflineWatch() {
  loadManual();

  // The browser's own signal works everywhere and is instant; on native the
  // Network plugin is authoritative because a WebView can report online while
  // the device has no route.
  const applyBrowser = () => {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      noNetwork = !navigator.onLine;
      emit();
    }
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('online', applyBrowser);
    window.addEventListener('offline', applyBrowser);
    applyBrowser();
  }

  if (!isNativePlatform()) return;

  try {
    const status = await Network.getStatus();
    noNetwork = !status.connected;
    emit();
    Network.addListener('networkStatusChange', (s) => {
      noNetwork = !s.connected;
      emit();
    });
  } catch (e) {
    // Plugin unavailable — fall back to the browser signal already wired above.
  }
}
